/**
 * Icônes des marqueurs, dessinées sur canvas au démarrage.
 *
 * Une forme par catégorie, une couleur par statut : la forme reste lisible en
 * noir et blanc et pour un daltonien, là où la couleur seule ne distinguerait
 * plus rien. Les GMF sont des cercles — dessinés par MapLibre lui-même
 * (couche `circle`), sans passer par ici.
 *
 *   ▲ triangle     GMF-U
 *   ■ carré « H »  hôpital
 *
 * Générées plutôt que chargées : 8 images (2 formes × 4 statuts) tiennent en
 * quelques lignes de canvas et évitent 8 fichiers à servir et à garder en
 * phase avec la palette.
 */

import { STATUS_COLORS } from '../domain/constants.js'

const SIZE = 44 // px, rendu en pixelRatio 2 -> 22 px logiques
const PAD = 6 // marge : le contour ne doit pas être rogné par le bord du canvas
const STROKE = 4.5

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function drawSquare(ctx, fill) {
  const w = SIZE - 2 * PAD
  ctx.fillStyle = fill
  roundedRect(ctx, PAD, PAD, w, w, 6)
  ctx.fill()
  ctx.stroke()

  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `bold ${Math.round(w * 0.6)}px -apple-system,'Segoe UI',Roboto,Arial,sans-serif`
  // +1 : le centre optique d'une capitale est sous son centre géométrique.
  ctx.fillText('H', SIZE / 2, SIZE / 2 + 1)
}

function drawTriangle(ctx, fill) {
  const top = PAD + 1
  const bottom = SIZE - PAD - 1
  ctx.fillStyle = fill
  ctx.beginPath()
  ctx.moveTo(SIZE / 2, top)
  ctx.lineTo(SIZE - PAD, bottom)
  ctx.lineTo(PAD, bottom)
  ctx.closePath()
  ctx.fill()
  ctx.stroke()
}

function shapeIcon(kind, fill) {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d indisponible')

  ctx.clearRect(0, 0, SIZE, SIZE)
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = STROKE

  if (kind === 'square') drawSquare(ctx, fill)
  else drawTriangle(ctx, fill)

  return ctx.getImageData(0, 0, SIZE, SIZE)
}

/** Enregistre les 8 icônes. À appeler une fois, sur l'événement `load`. */
export function addShapeImages(map) {
  for (const [status, color] of Object.entries(STATUS_COLORS)) {
    for (const [prefix, shape] of [['tri', 'triangle'], ['sq', 'square']]) {
      const name = `${prefix}-${status}`
      if (map.hasImage(name)) continue
      map.addImage(name, shapeIcon(shape, color), { pixelRatio: 2 })
    }
  }
}

/** Expressions qui choisissent l'icône d'après le statut. */
export const TRIANGLE_IMAGE = [
  'match', ['get', 'status'],
  'client', 'tri-client', 'gratuit', 'tri-gratuit', 'conversion', 'tri-conversion', 'tri-prospect',
]

export const SQUARE_IMAGE = [
  'match', ['get', 'status'],
  'client', 'sq-client', 'gratuit', 'sq-gratuit', 'conversion', 'sq-conversion', 'sq-prospect',
]
